import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTrip } from "@/hooks/useTrips";
import { useEnrichAllFlights } from "@/hooks/useFlightEnrichment";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Eye, Plus, Plane, Hotel, Car, Calendar, Ship, RefreshCw, ImageIcon, DollarSign, Paperclip } from "lucide-react";
import { FlightCard } from "@/components/trips/items/FlightCard";
import { HotelCard } from "@/components/trips/items/HotelCard";
import { CarCard } from "@/components/trips/items/CarCard";
import { ActivityCard } from "@/components/trips/items/ActivityCard";
import { CruiseCard } from "@/components/trips/items/CruiseCard";
import { AddFlightDialog } from "@/components/trips/dialogs/AddFlightDialog";
import { AddHotelDialog } from "@/components/trips/dialogs/AddHotelDialog";
import { AddCarDialog } from "@/components/trips/dialogs/AddCarDialog";
import { AddActivityDialog } from "@/components/trips/dialogs/AddActivityDialog";
import { AddCruiseDialog } from "@/components/trips/dialogs/AddCruiseDialog";
import { EditTripDialog } from "@/components/trips/EditTripDialog";
import { PhotoPickerDialog } from "@/components/trips/dialogs/PhotoPickerDialog";
import { AttachmentsManagerDialog } from "@/components/trips/dialogs/AttachmentsManagerDialog";
import { useTripAttachments } from "@/hooks/useAttachments";
import { FlightSegment, HotelReservation, HotelPricing, CarReservation, Activity, CruiseReservation, TripData } from "@/hooks/useTrips";
// Native date parsing helpers to avoid date-fns locale issues
function safeParseDateString(dateStr: string | undefined | null): Date | null {
  if (!dateStr || typeof dateStr !== 'string') return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

function nativeDifferenceInHours(later: Date, earlier: Date): number {
  return Math.floor((later.getTime() - earlier.getTime()) / (1000 * 60 * 60));
}

// Helper to parse datetime from separate date and time fields for sorting
function parseDateTimeForSorting(date?: string, time?: string): Date {
  if (!date) return new Date(0);

  // If time looks like a full ISO datetime, use it directly
  if (time && time.includes('T')) {
    const parsed = safeParseDateString(time);
    if (parsed) return parsed;
  }

  // If we have separate date and time, combine them
  if (time && /^\d{1,2}:\d{2}/.test(time)) {
    const combined = `${date}T${time.slice(0, 5)}:00`;
    const parsed = safeParseDateString(combined);
    if (parsed) return parsed;
  }

  // Fall back to just the date
  const parsed = safeParseDateString(date);
  return parsed || new Date(0);
}

const TripDetail = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { data: trip, isLoading: tripLoading } = useTrip(tripId);
  const enrichAllFlights = useEnrichAllFlights();

  const [showEditTrip, setShowEditTrip] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [showAddFlight, setShowAddFlight] = useState(false);
  const [showAddHotel, setShowAddHotel] = useState(false);
  const [showAddCar, setShowAddCar] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showAddCruise, setShowAddCruise] = useState(false);
  const [showAttachmentsManager, setShowAttachmentsManager] = useState(false);

  // Fetch attachment count for badge
  const { data: attachments } = useTripAttachments(tripId);
  const attachmentCount = attachments?.length || 0;

  // Extract trip data - must be before useMemo to maintain hook order
  const tripData = trip?.trip_data as TripData | null;
  const hotelPricings = tripData?.hotel_pricings || [];

  // Sort all items by date and time, preserving original indices for mutations
  const sortedFlights = useMemo(() => {
    return (tripData?.flights || [])
      .map((flight, originalIndex) => ({ item: flight, originalIndex }))
      .sort((a, b) => {
        const dateA = parseDateTimeForSorting(a.item.departure_date, a.item.departure_time);
        const dateB = parseDateTimeForSorting(b.item.departure_date, b.item.departure_time);
        return dateA.getTime() - dateB.getTime();
      });
  }, [tripData?.flights]);

  const sortedHotels = useMemo(() => {
    return (tripData?.hotels || [])
      .map((hotel, originalIndex) => ({ item: hotel, originalIndex }))
      .sort((a, b) => {
        const dateA = parseDateTimeForSorting(a.item.check_in_date, a.item.check_in_time);
        const dateB = parseDateTimeForSorting(b.item.check_in_date, b.item.check_in_time);
        return dateA.getTime() - dateB.getTime();
      });
  }, [tripData?.hotels]);

  const sortedCars = useMemo(() => {
    return (tripData?.cars || [])
      .map((car, originalIndex) => ({ item: car, originalIndex }))
      .sort((a, b) => {
        const dateA = parseDateTimeForSorting(a.item.pickup_date, a.item.pickup_time);
        const dateB = parseDateTimeForSorting(b.item.pickup_date, b.item.pickup_time);
        return dateA.getTime() - dateB.getTime();
      });
  }, [tripData?.cars]);

  const sortedActivities = useMemo(() => {
    return (tripData?.activities || [])
      .map((activity, originalIndex) => ({ item: activity, originalIndex }))
      .sort((a, b) => {
        const dateA = parseDateTimeForSorting(a.item.date, a.item.time);
        const dateB = parseDateTimeForSorting(b.item.date, b.item.time);
        return dateA.getTime() - dateB.getTime();
      });
  }, [tripData?.activities]);

  const sortedCruises = useMemo(() => {
    return (tripData?.cruises || [])
      .map((cruise, originalIndex) => ({ item: cruise, originalIndex }))
      .sort((a, b) => {
        const dateA = parseDateTimeForSorting(a.item.departure_date, a.item.departure_time);
        const dateB = parseDateTimeForSorting(b.item.departure_date, b.item.departure_time);
        return dateA.getTime() - dateB.getTime();
      });
  }, [tripData?.cruises]);

  // Helper to find pricing for a hotel
  const getHotelPricing = (hotel: HotelReservation) => {
    return hotelPricings.find(
      (p) => p.hotel_name === hotel.hotel_name && p.check_in_date === hotel.check_in_date
    );
  };

  // Check if any flight departs within 24 hours - MUST be before any early returns
  const hasFlightWithin24Hours = useMemo(() => {
    const now = new Date();
    return sortedFlights.some(({ item: flight }) => {
      if (!flight.departure_time && !flight.departure_date) return false;
      try {
        const departureDate = parseDateTimeForSorting(flight.departure_date, flight.departure_time);
        const hoursUntilDeparture = nativeDifferenceInHours(departureDate, now);
        return hoursUntilDeparture <= 24 && hoursUntilDeparture >= -24;
      } catch {
        return false;
      }
    });
  }, [sortedFlights]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  if (authLoading || tripLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Trip Not Found</h1>
            <Link to="/trips">
              <Button>Back to Trips</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Calculate trip stats
  const totalFlights = sortedFlights.length;
  const totalHotels = sortedHotels.length;
  const totalActivities = sortedActivities.length;
  const tripDuration = trip.start_date && trip.end_date
    ? Math.ceil((new Date(trip.end_date).getTime() - new Date(trip.start_date).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero Header with Gradient */}
      <div className="gradient-hero text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div>
              <Link to="/trips" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 text-sm transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to trips
              </Link>
              <h1 className="text-3xl font-bold mb-2">{trip.display_name}</h1>
              <div className="flex flex-wrap gap-4 text-white/80">
                {trip.destination && (
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {trip.destination}
                  </div>
                )}
                {trip.start_date && trip.end_date && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(trip.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - {new Date(trip.end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="hero-outline" size="sm" onClick={() => setShowPhotoPicker(true)}>
                <ImageIcon className="h-4 w-4 mr-2" />
                Cover Photo
              </Button>
              <Button variant="hero-outline" size="sm" onClick={() => setShowEditTrip(true)}>
                Edit Trip
              </Button>
              <Link to={`/preview/${tripId}`}>
                <Button variant="hero" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  View Full Itinerary
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-white/70 text-sm mb-1">Duration</div>
              <div className="text-2xl font-semibold">{tripDuration} days</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-white/70 text-sm mb-1">Flights</div>
              <div className="text-2xl font-semibold">{totalFlights}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-white/70 text-sm mb-1">Hotels</div>
              <div className="text-2xl font-semibold">{totalHotels}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-white/70 text-sm mb-1">Activities</div>
              <div className="text-2xl font-semibold">{totalActivities}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-card border-b border-border sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-2 sm:gap-6 overflow-x-auto whitespace-nowrap">
            <div className="px-4 py-3 text-primary border-b-2 border-primary font-medium">
              Timeline
            </div>
            <Link
              to={`/trips/${tripId}/costs`}
              className="px-4 py-3 text-muted-foreground hover:text-foreground border-b-2 border-transparent transition-colors"
            >
              Costs
            </Link>
            <button
              onClick={() => setShowAttachmentsManager(true)}
              className="px-4 py-3 text-muted-foreground hover:text-foreground border-b-2 border-transparent transition-colors flex items-center gap-2"
            >
              Attachments
              {attachmentCount > 0 && (
                <span className="bg-accent text-accent-foreground rounded-full px-2 py-0.5 text-xs">
                  {attachmentCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full">

        {/* Flights Section */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Plane className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Flights</h2>
              <span className="text-muted-foreground">({sortedFlights.length})</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {sortedFlights.length > 0 && hasFlightWithin24Hours && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => enrichAllFlights.mutate({ tripId: tripId!, flights: sortedFlights.map(f => f.item) })}
                  disabled={enrichAllFlights.isPending}
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${enrichAllFlights.isPending ? 'animate-spin' : ''}`} />
                  Update Flight Status
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowAddFlight(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Flight
              </Button>
            </div>
          </div>
          {sortedFlights.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 border border-dashed rounded-lg">
              No flights added yet
            </p>
          ) : (
            <div className="grid gap-4">
              {sortedFlights.map(({ item: flight, originalIndex }) => (
                <FlightCard
                  key={originalIndex}
                  flight={flight}
                  allFlights={tripData?.flights || []}
                  tripId={tripId!}
                  index={originalIndex}
                  tripPassengerName={tripData?.passenger_name}
                />
              ))}
            </div>
          )}
        </section>

        {/* Hotels Section */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Hotel className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Hotels</h2>
              <span className="text-muted-foreground">({sortedHotels.length})</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowAddHotel(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Hotel
            </Button>
          </div>
          {sortedHotels.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 border border-dashed rounded-lg">
              No hotels added yet
            </p>
          ) : (
            <div className="grid gap-4">
              {sortedHotels.map(({ item: hotel, originalIndex }) => (
                <HotelCard
                  key={originalIndex}
                  hotel={hotel}
                  tripId={tripId!}
                  index={originalIndex}
                  pricing={getHotelPricing(hotel)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Cars Section */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Car className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Car Rentals</h2>
              <span className="text-muted-foreground">({sortedCars.length})</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowAddCar(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Car
            </Button>
          </div>
          {sortedCars.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 border border-dashed rounded-lg">
              No car rentals added yet
            </p>
          ) : (
            <div className="grid gap-4">
              {sortedCars.map(({ item: car, originalIndex }) => (
                <CarCard key={originalIndex} car={car} tripId={tripId!} index={originalIndex} />
              ))}
            </div>
          )}
        </section>

        {/* Cruises Section */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Ship className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Cruises</h2>
              <span className="text-muted-foreground">({sortedCruises.length})</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowAddCruise(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Cruise
            </Button>
          </div>
          {sortedCruises.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 border border-dashed rounded-lg">
              No cruises added yet
            </p>
          ) : (
            <div className="grid gap-4">
              {sortedCruises.map(({ item: cruise, originalIndex }) => (
                <CruiseCard key={originalIndex} cruise={cruise} tripId={tripId!} index={originalIndex} />
              ))}
            </div>
          )}
        </section>

        {/* Activities Section */}
        <section className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Activities</h2>
              <span className="text-muted-foreground">({sortedActivities.length})</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowAddActivity(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Activity
            </Button>
          </div>
          {sortedActivities.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 border border-dashed rounded-lg">
              No activities added yet
            </p>
          ) : (
            <div className="grid gap-4">
              {sortedActivities.map(({ item: activity, originalIndex }) => (
                <ActivityCard key={originalIndex} activity={activity} tripId={tripId!} index={originalIndex} />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />

      {/* Dialogs */}
      <EditTripDialog trip={trip} open={showEditTrip} onOpenChange={setShowEditTrip} />
      <PhotoPickerDialog
        tripId={tripId!}
        destination={trip.destination}
        currentPhotoId={trip.cover_photo_id}
        currentPositionY={trip.cover_photo_position_y}
        open={showPhotoPicker}
        onOpenChange={setShowPhotoPicker}
      />
      <AddFlightDialog tripId={tripId!} open={showAddFlight} onOpenChange={setShowAddFlight} />
      <AddHotelDialog tripId={tripId!} open={showAddHotel} onOpenChange={setShowAddHotel} />
      <AddCarDialog tripId={tripId!} open={showAddCar} onOpenChange={setShowAddCar} />
      <AddActivityDialog tripId={tripId!} open={showAddActivity} onOpenChange={setShowAddActivity} />
      <AddCruiseDialog tripId={tripId!} open={showAddCruise} onOpenChange={setShowAddCruise} />
      <AttachmentsManagerDialog trip={trip} open={showAttachmentsManager} onOpenChange={setShowAttachmentsManager} />
    </div>
  );
};

export default TripDetail;
